import { useMemo } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { Variable } from 'api/data/variables';
import { DraggableCard, TimesUsed } from 'components/Forms';
import { Svgs } from 'environment';
import { AddVariableIcon, CardLabel } from './DraggableFormVariableCard.style';
import { Icon } from 'components/UI/Icons';
import { Asterisk } from 'components/UI/Asterisk';
import { Typography } from 'components/UI/Typography';
import { computeFormElementFromVariable } from 'helpers/forms';
import { useFormsMap, useAppendFormElement, useTranslation } from 'hooks/store';

interface Props {
	provided: DraggableProvided;
	variable: Variable;
	canAppend: boolean;
}

export function DraggableFormVariableCard({ provided, variable, canAppend }: Props) {
	const formsMap = useFormsMap();

	const appendFormElement = useAppendFormElement();

	const { name, label, obligatory } = variable;
	const { translate } = useTranslation();
	const element = computeFormElementFromVariable(variable);

	const timesUsed = useMemo(() => {
		let times = 0;

		Object.values(formsMap).forEach(form => {
			if (form.initial.usedVariables.includes(variable.name)) times++;
		});

		return times;
	}, [formsMap]);

	return (
		<DraggableCard
			key={name}
			data-test-id={label}
			ref={provided.innerRef}
			{...provided.draggableProps}
			{...provided.dragHandleProps}
		>
			<CardLabel title={label}>{label}</CardLabel>
			{obligatory && <Asterisk paddingLeft />}
			{canAppend && (
				<AddVariableIcon className="add-variable-icon">
					<Icon
						showTooltip={false}
						svg={Svgs.Add}
						onClick={() => appendFormElement({ element })}
						active
					/>
					{timesUsed > 0 && (
						<TimesUsed className="times-variable-used">
							<Typography.Paragraph>{`${translate(
								dict => dict.formDesigner.usedIn
							)} ${timesUsed} ${
								timesUsed > 1
									? translate(dict => dict.projectTabs.forms).toLowerCase()
									: translate(dict => dict.formDesigner.form).toLowerCase()
							}`}</Typography.Paragraph>
						</TimesUsed>
					)}
				</AddVariableIcon>
			)}
		</DraggableCard>
	);
}
